import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { adminAPI } from '../api/api';
import { ArrowLeft, Building2, Users as UsersIcon, Activity as ActivityIcon, Clock, UserPlus, UserMinus, ArrowRightLeft, Pause, Play, XCircle, Shield, Edit3, Eye, Settings, Mail, Sparkles, Search, Copy, Check, AlertCircle, ExternalLink } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Card, SectionHeader, Btn, Badge, PlanBadge, StatusDot, Modal, TextField, SelectField, formatDate, formatDateTime, Spinner } from '../components/admin/AdminUI';

const ROLE_LABELS = { owner: 'Propriétaire', admin: 'Administrateur', manager: 'Manager', editor: 'Éditeur', viewer: 'Lecteur' };
const ROLE_TONES = { owner: 'violet', admin: 'blue', manager: 'emerald', editor: 'amber', viewer: 'gray' };
const ROLE_ICONS = { owner: Shield, admin: Shield, manager: Settings, editor: Edit3, viewer: Eye };

const AdminOrgDetail = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Add by email modal
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ email: '', role: 'editor' });
  const [adding, setAdding] = useState(false);

  // Remove
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);

  // Move
  const [moveTarget, setMoveTarget] = useState(null);
  const [allOrgs, setAllOrgs] = useState([]);
  const [moveForm, setMoveForm] = useState({ target_org_id: '', role: 'editor' });
  const [moving, setMoving] = useState(false);

  // Edit role
  const [editTarget, setEditTarget] = useState(null); // membership row
  const [editRole, setEditRole] = useState('editor');
  const [savingRole, setSavingRole] = useState(false);

  // ── Pick from existing Supabase users ──
  const [showPicker, setShowPicker] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerUsers, setPickerUsers] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerRole, setPickerRole] = useState('editor');
  const [pickerAddingId, setPickerAddingId] = useState(null);

  // ── Beta tester bulk invite ──
  const [showBeta, setShowBeta] = useState(false);
  const [betaEmails, setBetaEmails] = useState('');
  const [betaRole, setBetaRole] = useState('editor');
  const [betaShared, setBetaShared] = useState(true);   // one shared password for all
  const [betaPassword, setBetaPassword] = useState(''); // empty → backend auto-generates
  const [betaPrefix, setBetaPrefix] = useState('Beta Tester');
  const [betaLoading, setBetaLoading] = useState(false);
  const [betaResults, setBetaResults] = useState(null); // null until first submit
  const [copiedKey, setCopiedKey] = useState(null);

  const fetchData = useCallback(async () => {
    try { setLoading(true); setData(await adminAPI.getOrgDetail(orgId)); }
    catch { toast({ title: 'Erreur', description: 'Impossible de charger les détails', variant: 'destructive' }); }
    finally { setLoading(false); }
  }, [orgId, toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (moveTarget && allOrgs.length === 0) adminAPI.listOrganizations().then(setAllOrgs).catch(() => {});
  }, [moveTarget, allOrgs.length]);

  const handleAddByEmail = async (e) => {
    e.preventDefault(); setAdding(true);
    try {
      await adminAPI.addMemberByEmail(orgId, addForm);
      toast({ title: 'Membre ajouté', description: addForm.email });
      setShowAdd(false); setAddForm({ email: '', role: 'editor' }); fetchData();
    } catch (err) { toast({ title: 'Erreur', description: err.response?.data?.detail || 'Échec', variant: 'destructive' }); }
    finally { setAdding(false); }
  };

  const handleRemove = async () => {
    if (!removeTarget) return; setRemoving(true);
    try {
      await adminAPI.removeMember(orgId, removeTarget.id);
      toast({ title: 'Membre retiré', description: removeTarget.full_name });
      setRemoveTarget(null); fetchData();
    } catch (err) { toast({ title: 'Erreur', description: err.response?.data?.detail || 'Échec', variant: 'destructive' }); }
    finally { setRemoving(false); }
  };

  const handleMove = async (e) => {
    e.preventDefault(); setMoving(true);
    try {
      await adminAPI.moveMember(orgId, moveTarget.id, moveForm);
      toast({ title: 'Membre déplacé' });
      setMoveTarget(null); setMoveForm({ target_org_id: '', role: 'editor' }); fetchData();
    } catch (err) { toast({ title: 'Erreur', description: err.response?.data?.detail || 'Échec', variant: 'destructive' }); }
    finally { setMoving(false); }
  };

  const handleSaveRole = async (e) => {
    e.preventDefault(); setSavingRole(true);
    try {
      await adminAPI.updateMember(orgId, editTarget.id, { role: editRole });
      toast({ title: 'Rôle mis à jour' });
      setEditTarget(null); fetchData();
    } catch (err) { toast({ title: 'Erreur', description: err.response?.data?.detail || 'Échec', variant: 'destructive' }); }
    finally { setSavingRole(false); }
  };

  const toggleMemberStatus = async (m) => {
    const next = m.status === 'active' ? 'disabled' : 'active';
    try {
      await adminAPI.updateMember(orgId, m.id, { status: next });
      toast({ title: next === 'disabled' ? 'Membre désactivé' : 'Membre réactivé' });
      fetchData();
    } catch (err) { toast({ title: 'Erreur', description: err.response?.data?.detail || 'Échec', variant: 'destructive' }); }
  };

  const toggleOrgStatus = async () => {
    const next = data.org.status === 'active' ? 'suspended' : 'active';
    try {
      await adminAPI.setOrgStatus(orgId, next);
      toast({ title: next === 'suspended' ? 'Organisation suspendue' : 'Organisation réactivée' });
      fetchData();
    } catch (err) { toast({ title: 'Erreur', description: err.response?.data?.detail || 'Échec', variant: 'destructive' }); }
  };

  const cancelSub = async () => {
    if (!window.confirm("Annuler immédiatement l'abonnement Stripe ? L'organisation repassera en plan Starter.")) return;
    try {
      await adminAPI.cancelSubscription(orgId);
      toast({ title: 'Abonnement annulé' });
      fetchData();
    } catch (err) { toast({ title: 'Erreur', description: err.response?.data?.detail || 'Échec', variant: 'destructive' }); }
  };

  // ── Picker: load user list when opened (or search changes) ──
  useEffect(() => {
    if (!showPicker) return;
    let cancelled = false;
    setPickerLoading(true);
    const t = setTimeout(async () => {
      try {
        const data = await adminAPI.listUsers({ search: pickerSearch || undefined, limit: 100 });
        if (!cancelled) setPickerUsers(data.users || []);
      } catch {
        if (!cancelled) toast({ title: 'Erreur', description: 'Impossible de charger les utilisateurs', variant: 'destructive' });
      } finally {
        if (!cancelled) setPickerLoading(false);
      }
    }, pickerSearch ? 250 : 0);
    return () => { cancelled = true; clearTimeout(t); };
  }, [showPicker, pickerSearch, toast]);

  const handlePickUser = async (u) => {
    setPickerAddingId(u.id);
    try {
      await adminAPI.addMember(orgId, { user_id: u.id, role: pickerRole });
      toast({ title: 'Membre ajouté', description: u.email || u.full_name });
      fetchData();
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err.response?.data?.detail || 'Échec de l\'ajout',
        variant: 'destructive',
      });
    } finally {
      setPickerAddingId(null);
    }
  };

  const handleBetaSubmit = async (e) => {
    e.preventDefault();
    const emails = betaEmails
      .split(/[\s,;]+/)
      .map(s => s.trim())
      .filter(Boolean);
    if (!emails.length) {
      toast({ title: 'Aucun email', description: 'Saisissez au moins un email.', variant: 'destructive' });
      return;
    }
    if (emails.length > 50) {
      toast({ title: 'Trop d\'emails', description: 'Maximum 50 par lot.', variant: 'destructive' });
      return;
    }
    setBetaLoading(true);
    try {
      const payload = {
        emails,
        role: betaRole,
        full_name_prefix: betaPrefix?.trim() || null,
      };
      if (betaShared && betaPassword.trim()) {
        payload.shared_password = betaPassword.trim();
      }
      const data = await adminAPI.betaInvite(orgId, payload);
      setBetaResults(data.results || []);
      const okCount = (data.results || []).filter(r => r.status === 'created' || r.status === 'existing_added').length;
      toast({ title: `${okCount}/${emails.length} bêta-testeurs ajoutés` });
      fetchData();
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err.response?.data?.detail || err.message || 'Échec',
        variant: 'destructive',
      });
    } finally {
      setBetaLoading(false);
    }
  };

  const closeBetaModal = () => {
    setShowBeta(false);
    // Reset only after close so users see the final state if they reopen mid-session
    setTimeout(() => {
      setBetaEmails('');
      setBetaResults(null);
      setBetaPassword('');
    }, 200);
  };

  const copyToClipboard = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    } catch {
      toast({ title: 'Erreur', description: 'Copie impossible', variant: 'destructive' });
    }
  };

  const copyAllBetaCredentials = () => {
    if (!betaResults) return;
    const lines = betaResults
      .filter(r => r.password)
      .map(r => `${r.email} / ${r.password}`);
    if (!lines.length) {
      toast({ title: 'Aucun identifiant à copier', description: 'Tous les comptes existaient déjà.' });
      return;
    }
    copyToClipboard(lines.join('\n'), 'all');
  };

  useEffect(() => { document.title = "Skilldeck — Admin · Détails organisation"; }, []);

  const org = data?.org;
  const subscription = data?.subscription;
  const members = data?.members || [];
  const auditLogs = data?.audit_logs || [];
  const planCode = subscription?.plan_code || org?.plan_code;

  return (
    <AdminLayout title="Détails organisation">
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">

        <button onClick={() => navigate('/admin/orgs')} className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white">
          <ArrowLeft className="h-4 w-4" /> Retour aux organisations
        </button>

        {loading ? (
          <div className="flex justify-center py-24"><Spinner /></div>
        ) : !org ? (
          <div className="text-center py-24 text-gray-500">Organisation introuvable</div>
        ) : (
          <>
            {/* Org header */}
            <Card>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-xl bg-violet-900/30 flex items-center justify-center shrink-0">
                    <Building2 className="h-6 w-6 text-violet-300" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-white">{org.name}</h2>
                    <p className="text-xs text-gray-500 font-mono mt-0.5">{org.slug}</p>
                    <div className="flex items-center gap-2 mt-2">
                      {planCode && <PlanBadge code={planCode} name={subscription?.plan_name} />}
                      <StatusDot status={org.status} />
                      {subscription?.status && <Badge tone="blue">{subscription.status}</Badge>}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Btn tone="ghost" onClick={toggleOrgStatus}>
                    {org.status === 'active' ? <><Pause className="h-3 w-3" /> Suspendre</> : <><Play className="h-3 w-3" /> Réactiver</>}
                  </Btn>
                  {subscription && (
                    <Btn tone="danger" onClick={cancelSub}><XCircle className="h-3 w-3" /> Annuler abonnement</Btn>
                  )}
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4 border-t border-[rgba(108,71,255,.1)]">
                <Stat label="Membres" value={members.length} />
                <Stat label="Créée le" value={formatDate(org.created_at)} small />
                {subscription && (
                  <>
                    <Stat label="Début période" value={formatDate(subscription.current_period_start)} small />
                    <Stat label="Renouvellement" value={formatDate(subscription.current_period_end)} small />
                  </>
                )}
              </div>
            </Card>

            {/* Members */}
            <Card padding="p-0">
              <div className="px-5 py-4 border-b border-[rgba(108,71,255,.12)] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <UsersIcon className="h-4 w-4 text-violet-300" />
                  <h3 className="text-sm font-semibold text-white">Membres ({members.length})</h3>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Btn tone="subtle" onClick={() => { setPickerSearch(''); setShowPicker(true); }}>
                    <Search className="h-3.5 w-3.5" /> Depuis utilisateurs
                  </Btn>
                  <Btn tone="subtle" onClick={() => setShowBeta(true)}>
                    <Sparkles className="h-3.5 w-3.5" /> Inviter bêta-testeurs
                  </Btn>
                  <Btn onClick={() => setShowAdd(true)}>
                    <UserPlus className="h-3.5 w-3.5" /> Ajouter par email
                  </Btn>
                </div>
              </div>
              {members.length === 0 ? (
                <div className="text-center py-10 text-gray-500 text-sm">Aucun membre</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[rgba(108,71,255,.1)] text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                        <th className="px-5 py-3">Membre</th>
                        <th className="px-5 py-3">Rôle</th>
                        <th className="px-5 py-3">Statut</th>
                        <th className="px-5 py-3">Dossiers</th>
                        <th className="px-5 py-3">Depuis</th>
                        <th className="px-5 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[rgba(108,71,255,.06)]">
                      {members.map((m) => {
                        const Icon = ROLE_ICONS[m.role] || Eye;
                        const tone = ROLE_TONES[m.role] || 'gray';
                        const name = m.full_name || (m.user_id ? m.user_id.slice(0, 8) + '…' : '—');
                        const initials = (name.split(' ').map(w => w[0]).join('') || '?').toUpperCase().slice(0, 2);
                        return (
                          <tr key={m.id} className="hover:bg-[rgba(108,71,255,.04)] transition-colors">
                            <td className="px-5 py-3">
                              <div className="flex items-center gap-3">
                                <div className="h-8 w-8 rounded-full bg-violet-900/30 flex items-center justify-center text-violet-200 font-semibold text-xs shrink-0">{initials}</div>
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-white truncate">{name}</p>
                                  <p className="text-[11px] text-gray-500 font-mono truncate">{m.user_id?.slice(0, 12)}…</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-5 py-3">
                              <Badge tone={tone}>
                                <Icon className="h-3 w-3 mr-1" /> {ROLE_LABELS[m.role] || m.role}
                              </Badge>
                            </td>
                            <td className="px-5 py-3"><StatusDot status={m.status} /></td>
                            <td className="px-5 py-3 text-sm text-gray-300">{m.dossier_count ?? 0}</td>
                            <td className="px-5 py-3 text-xs text-gray-400">{formatDate(m.created_at)}</td>
                            <td className="px-5 py-3">
                              <div className="flex items-center justify-end gap-1.5">
                                <Btn tone="subtle" onClick={() => { setEditTarget(m); setEditRole(m.role); }} title="Modifier rôle"><Edit3 className="h-3 w-3" /></Btn>
                                <Btn tone="subtle" onClick={() => toggleMemberStatus(m)} title={m.status === 'active' ? 'Désactiver' : 'Réactiver'}>
                                  {m.status === 'active' ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                                </Btn>
                                <Btn tone="subtle" onClick={() => { setMoveTarget({ id: m.id, full_name: name }); setMoveForm({ target_org_id: '', role: m.role }); }} title="Déplacer">
                                  <ArrowRightLeft className="h-3 w-3" />
                                </Btn>
                                <Btn tone="danger" onClick={() => setRemoveTarget({ id: m.id, full_name: name })} title="Retirer">
                                  <UserMinus className="h-3 w-3" />
                                </Btn>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {/* Audit */}
            <Card padding="p-0">
              <div className="px-5 py-4 border-b border-[rgba(108,71,255,.12)] flex items-center gap-2">
                <ActivityIcon className="h-4 w-4 text-violet-300" />
                <h3 className="text-sm font-semibold text-white">Journal d'activité (50 dernières)</h3>
              </div>
              {auditLogs.length === 0 ? (
                <div className="text-center py-10 text-gray-500 text-sm">Aucune activité</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[rgba(108,71,255,.1)] text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                        <th className="px-5 py-3">Date</th>
                        <th className="px-5 py-3">Acteur</th>
                        <th className="px-5 py-3">Action</th>
                        <th className="px-5 py-3">Entité</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[rgba(108,71,255,.06)]">
                      {auditLogs.map((e) => (
                        <tr key={e.id}>
                          <td className="px-5 py-3 text-xs text-gray-400 whitespace-nowrap">
                            <span className="inline-flex items-center gap-1.5"><Clock className="h-3 w-3" /> {formatDateTime(e.created_at)}</span>
                          </td>
                          <td className="px-5 py-3 text-sm text-gray-300">{e.actor_name || e.actor_user_id?.slice(0, 12) || '—'}</td>
                          <td className="px-5 py-3"><Badge tone="violet">{e.action}</Badge></td>
                          <td className="px-5 py-3 text-xs text-gray-500 font-mono">{e.entity_type} {e.entity_id?.slice(0, 8)}{e.entity_id ? '…' : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </>
        )}

        {/* Add member by email */}
        <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Ajouter un membre">
          <form onSubmit={handleAddByEmail} className="px-6 py-5 space-y-4">
            <TextField label="Email *" type="email" required value={addForm.email} onChange={(e) => setAddForm(f => ({ ...f, email: e.target.value }))} placeholder="user@example.com" />
            <SelectField label="Rôle" value={addForm.role} onChange={(e) => setAddForm(f => ({ ...f, role: e.target.value }))}>
              {Object.entries(ROLE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </SelectField>
            <p className="text-[11px] text-gray-500"><Mail className="inline h-3 w-3 mr-1" /> L'utilisateur doit déjà avoir un compte Skilldeck.</p>
            <div className="flex justify-end gap-3 pt-2">
              <Btn type="button" tone="ghost" onClick={() => setShowAdd(false)}>Annuler</Btn>
              <Btn type="submit" disabled={adding}>{adding ? 'Ajout…' : 'Ajouter'}</Btn>
            </div>
          </form>
        </Modal>

        {/* Edit role */}
        <Modal open={!!editTarget} onClose={() => setEditTarget(null)} title="Modifier le rôle">
          <form onSubmit={handleSaveRole} className="px-6 py-5 space-y-4">
            <p className="text-sm text-gray-400">Membre : <span className="text-white">{editTarget?.full_name || '—'}</span></p>
            <SelectField label="Nouveau rôle" value={editRole} onChange={(e) => setEditRole(e.target.value)}>
              {Object.entries(ROLE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </SelectField>
            <div className="flex justify-end gap-3 pt-2">
              <Btn type="button" tone="ghost" onClick={() => setEditTarget(null)}>Annuler</Btn>
              <Btn type="submit" disabled={savingRole}>{savingRole ? '…' : 'Enregistrer'}</Btn>
            </div>
          </form>
        </Modal>

        {/* Remove confirm */}
        <Modal open={!!removeTarget} onClose={() => setRemoveTarget(null)} title="Retirer le membre" danger maxWidth="max-w-sm">
          <div className="px-6 py-5">
            <p className="text-sm text-gray-300 mb-5">Retirer <span className="font-semibold text-white">"{removeTarget?.full_name}"</span> de cette organisation ?</p>
            <div className="flex gap-3">
              <Btn tone="ghost" className="flex-1 justify-center" onClick={() => setRemoveTarget(null)}>Annuler</Btn>
              <Btn tone="danger" className="flex-1 justify-center" onClick={handleRemove} disabled={removing}>{removing ? 'Suppression…' : 'Retirer'}</Btn>
            </div>
          </div>
        </Modal>

        {/* ── Pick from existing Supabase users ── */}
        <Modal open={showPicker} onClose={() => setShowPicker(false)} title="Ajouter un utilisateur existant" maxWidth="max-w-2xl">
          <div className="px-6 py-5 space-y-4">
            <p className="text-xs text-gray-500">
              Sélectionnez un utilisateur Supabase déjà créé. Les utilisateurs déjà membres de cette organisation n'apparaissent pas.
            </p>
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500" />
                <input
                  type="text"
                  value={pickerSearch}
                  onChange={(e) => setPickerSearch(e.target.value)}
                  placeholder="Rechercher par nom ou email…"
                  className="w-full pl-9 pr-3 py-2 bg-[#05050f] border border-[rgba(108,71,255,.2)] rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-violet-500/60"
                  autoFocus
                />
              </div>
              <div className="w-40">
                <SelectField value={pickerRole} onChange={(e) => setPickerRole(e.target.value)}>
                  {Object.entries(ROLE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </SelectField>
              </div>
            </div>

            <div className="max-h-80 overflow-y-auto border border-[rgba(108,71,255,.15)] rounded-lg bg-[#05050f]">
              {pickerLoading ? (
                <div className="flex justify-center py-10"><Spinner className="h-6 w-6" /></div>
              ) : pickerUsers.length === 0 ? (
                <div className="text-center py-10 text-gray-500 text-sm">
                  {pickerSearch ? 'Aucun utilisateur trouvé' : 'Aucun utilisateur disponible'}
                </div>
              ) : (
                <ul className="divide-y divide-[rgba(108,71,255,.08)]">
                  {pickerUsers.map((u) => {
                    const alreadyMember = members.some(m => m.user_id === u.id);
                    const initials = (u.full_name || u.email || '?').split(/[ ._-]/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';
                    return (
                      <li key={u.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-[rgba(108,71,255,.04)] transition-colors">
                        <div className="h-8 w-8 rounded-full bg-violet-900/30 flex items-center justify-center text-violet-200 font-semibold text-xs shrink-0">
                          {initials}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-white truncate">{u.full_name || '—'}</p>
                          <p className="text-[11px] text-gray-500 truncate">{u.email || u.id?.slice(0, 12) + '…'}</p>
                        </div>
                        <div className="text-[11px] text-gray-500 mr-2">{u.org_count ?? 0} org{(u.org_count ?? 0) > 1 ? 's' : ''}</div>
                        {alreadyMember ? (
                          <Badge tone="emerald">Déjà membre</Badge>
                        ) : (
                          <Btn
                            tone="subtle"
                            onClick={() => handlePickUser(u)}
                            disabled={pickerAddingId === u.id}
                          >
                            {pickerAddingId === u.id ? '…' : <><UserPlus className="h-3 w-3" /> Ajouter</>}
                          </Btn>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="flex justify-end pt-1">
              <Btn tone="ghost" onClick={() => setShowPicker(false)}>Fermer</Btn>
            </div>
          </div>
        </Modal>

        {/* ── Bulk beta-tester invite ── */}
        <Modal open={showBeta} onClose={closeBetaModal} title="Inviter des bêta-testeurs" maxWidth="max-w-2xl">
          {!betaResults ? (
            <form onSubmit={handleBetaSubmit} className="px-6 py-5 space-y-4">
              <div className="flex gap-2 items-start p-3 rounded-lg bg-violet-900/15 border border-violet-700/30">
                <Sparkles className="h-4 w-4 text-violet-300 mt-0.5 shrink-0" />
                <p className="text-xs text-violet-100 leading-relaxed">
                  Crée un compte Supabase (email auto-confirmé) pour chaque adresse,
                  les ajoute à cette organisation, et vous retourne les identifiants
                  à copier-coller dans vos emails de lancement bêta.
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">
                  Emails des bêta-testeurs *
                  <span className="text-gray-600 ml-1">(un par ligne, ou séparés par virgules)</span>
                </label>
                <textarea
                  required
                  value={betaEmails}
                  onChange={(e) => setBetaEmails(e.target.value)}
                  placeholder={'beta1@exemple.com\nbeta2@exemple.com\nbeta3@exemple.com'}
                  rows={6}
                  className="w-full px-3 py-2 bg-[#05050f] border border-[rgba(108,71,255,.2)] rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-violet-500/60 font-mono"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <SelectField label="Rôle attribué" value={betaRole} onChange={(e) => setBetaRole(e.target.value)}>
                  {Object.entries(ROLE_LABELS).filter(([v]) => v !== 'owner').map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </SelectField>
                <TextField
                  label="Préfixe de nom"
                  hint="(optionnel)"
                  value={betaPrefix}
                  onChange={(e) => setBetaPrefix(e.target.value)}
                  placeholder="Beta Tester"
                />
              </div>

              <div className="space-y-2 pt-2 border-t border-[rgba(108,71,255,.12)]">
                <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={betaShared}
                    onChange={(e) => setBetaShared(e.target.checked)}
                    className="rounded border-violet-700 bg-[#05050f] text-violet-500 focus:ring-violet-500/40"
                  />
                  Utiliser le même mot de passe pour tous les bêta-testeurs
                </label>
                {betaShared && (
                  <TextField
                    type="text"
                    value={betaPassword}
                    onChange={(e) => setBetaPassword(e.target.value)}
                    placeholder="Laisser vide pour génération automatique"
                    hint="(min 8 caractères)"
                  />
                )}
                {!betaShared && (
                  <p className="text-[11px] text-gray-500 italic">
                    Un mot de passe unique sera généré pour chaque bêta-testeur.
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Btn type="button" tone="ghost" onClick={closeBetaModal}>Annuler</Btn>
                <Btn type="submit" disabled={betaLoading}>
                  {betaLoading ? 'Création en cours…' : <><Sparkles className="h-3.5 w-3.5" /> Créer les comptes</>}
                </Btn>
              </div>
            </form>
          ) : (
            <div className="px-6 py-5 space-y-4">
              <div className="flex gap-2 items-start p-3 rounded-lg bg-amber-900/15 border border-amber-700/30">
                <AlertCircle className="h-4 w-4 text-amber-300 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-100 leading-relaxed">
                  <strong>Copiez les mots de passe maintenant.</strong> Ils ne pourront pas être affichés à nouveau.
                  Les comptes existants ne reçoivent pas de nouveau mot de passe.
                </p>
              </div>

              <div className="border border-[rgba(108,71,255,.15)] rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[rgba(108,71,255,.12)] bg-[#0a0a22] text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                      <th className="px-3 py-2">Email</th>
                      <th className="px-3 py-2">Statut</th>
                      <th className="px-3 py-2">Mot de passe</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[rgba(108,71,255,.06)]">
                    {betaResults.map((r, i) => {
                      const tone = r.status === 'created' ? 'violet'
                        : r.status === 'existing_added' ? 'blue'
                        : r.status === 'already_member' ? 'emerald'
                        : 'gray';
                      const label = r.status === 'created' ? 'Créé'
                        : r.status === 'existing_added' ? 'Ajouté'
                        : r.status === 'already_member' ? 'Déjà membre'
                        : 'Erreur';
                      return (
                        <tr key={i} className={r.status === 'error' ? 'bg-red-900/10' : ''}>
                          <td className="px-3 py-2 text-xs text-gray-200 font-mono">{r.email}</td>
                          <td className="px-3 py-2">
                            {r.status === 'error'
                              ? <Badge tone="red"><AlertCircle className="h-3 w-3 mr-1" />Erreur</Badge>
                              : <Badge tone={tone}>{label}</Badge>}
                          </td>
                          <td className="px-3 py-2 text-xs font-mono text-gray-300">
                            {r.password ? r.password : (r.error ? <span className="text-red-300">{r.error}</span> : <span className="text-gray-600">—</span>)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {r.password && (
                              <button
                                type="button"
                                onClick={() => copyToClipboard(`${r.email} / ${r.password}`, `row-${i}`)}
                                className="inline-flex items-center gap-1 text-xs text-violet-300 hover:text-violet-200"
                                title="Copier email + mot de passe"
                              >
                                {copiedKey === `row-${i}` ? <><Check className="h-3 w-3" /> Copié</> : <><Copy className="h-3 w-3" /> Copier</>}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-between items-center gap-3 pt-2">
                <Btn tone="subtle" onClick={copyAllBetaCredentials}>
                  {copiedKey === 'all' ? <><Check className="h-3.5 w-3.5" /> Tout copié</> : <><Copy className="h-3.5 w-3.5" /> Copier toutes les identifiants</>}
                </Btn>
                <div className="flex gap-2">
                  <Btn tone="ghost" onClick={() => setBetaResults(null)}>Inviter d'autres testeurs</Btn>
                  <Btn onClick={closeBetaModal}>Terminer</Btn>
                </div>
              </div>
            </div>
          )}
        </Modal>

        {/* Move */}
        <Modal open={!!moveTarget} onClose={() => setMoveTarget(null)} title="Déplacer le membre">
          <form onSubmit={handleMove} className="px-6 py-5 space-y-4">
            <p className="text-sm text-gray-400">Déplacer <span className="text-white">"{moveTarget?.full_name}"</span> vers une autre organisation.</p>
            <SelectField label="Organisation cible *" required value={moveForm.target_org_id} onChange={(e) => setMoveForm(f => ({ ...f, target_org_id: e.target.value }))}>
              <option value="">Sélectionner…</option>
              {allOrgs.filter(o => o.id !== orgId).map(o => <option key={o.id} value={o.id}>{o.name} ({o.slug})</option>)}
            </SelectField>
            <SelectField label="Rôle dans la nouvelle org" value={moveForm.role} onChange={(e) => setMoveForm(f => ({ ...f, role: e.target.value }))}>
              {Object.entries(ROLE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </SelectField>
            <div className="flex justify-end gap-3 pt-2">
              <Btn type="button" tone="ghost" onClick={() => setMoveTarget(null)}>Annuler</Btn>
              <Btn type="submit" disabled={moving}>{moving ? 'Déplacement…' : 'Déplacer'}</Btn>
            </div>
          </form>
        </Modal>
      </div>
    </AdminLayout>
  );
};

const Stat = ({ label, value, small }) => (
  <div>
    <p className="text-[11px] text-gray-500">{label}</p>
    <p className={`${small ? 'text-sm text-gray-300' : 'text-lg font-bold text-white'} mt-0.5`}>{value}</p>
  </div>
);

export default AdminOrgDetail;
